// Port of SuiteModel/WritingSprint.swift: a timed stretch of writing with an optional word goal, counted honestly —
// what was written and what was cut kept apart (each save adds a document's growth to one and its shrinkage to the
// other), so rewriting a paragraph shows as work, not as nothing — and `SprintLog`, this device's finished sprints,
// newest first (500 kept). Dates are milliseconds since 1970 so a sprint survives JSON (localStorage) unchanged.

export interface WritingSprint {
  id: string;
  projectID: string;
  projectTitle: string;
  /** When it began (ms). */
  started: number;
  /** Planned length in seconds. */
  duration: number;
  goal: number | null;
  added: number;
  removed: number;
  /** When it stopped (the planned end, or earlier if the writer stopped it). */
  ended: number | null;
}

/** `WritingSprint(…minutes:goal:)`: at least a minute; a goal of 0 or less is none. `seconds` overrides the length
 *  (tests run short sprints). */
export function newSprint(o: { id: string; projectID: string; projectTitle: string; started: number; minutes: number; goal?: number | null; seconds?: number }): WritingSprint {
  const goal = o.goal && o.goal > 0 ? Math.floor(o.goal) : null;
  const duration = o.seconds && o.seconds > 0 ? o.seconds : Math.max(1, Math.floor(o.minutes)) * 60;
  return { id: o.id, projectID: o.projectID, projectTitle: o.projectTitle, started: o.started, duration, goal, added: 0, removed: 0, ended: null };
}

export const plannedEnd = (s: WritingSprint) => s.started + s.duration * 1000;
export const sprintNet = (s: WritingSprint) => s.added - s.removed;
/** Seconds left at `now`. */
export const remaining = (s: WritingSprint, now: number) => Math.max(0, (plannedEnd(s) - now) / 1000);
/** How far through the time it is, 0…1. */
export const progress = (s: WritingSprint, now: number) => Math.min(1, Math.max(0, (now - s.started) / 1000 / s.duration));
export const isOver = (s: WritingSprint, now: number) => now >= plannedEnd(s);
export const goalMet = (s: WritingSprint) => (s.goal === null ? false : sprintNet(s) >= s.goal);

/** Words written a minute over the time actually spent (at least a minute, so a quick stop isn't a record). */
export function wordsPerMinute(s: WritingSprint): number {
  const end = s.ended ?? plannedEnd(s);
  return s.added / Math.max(1, (end - s.started) / 60_000);
}

/** `record(before:after:)`: one save's change in a document's words. */
export function recordSprint(s: WritingSprint, before: number, after: number): WritingSprint {
  return { ...s, added: s.added + Math.max(0, after - before), removed: s.removed + Math.max(0, before - after) };
}

/** `extendableFor`: how long after a session ends it can still be extended (ms); later, writing on is a new session. */
export const SPRINT_EXTENDABLE_FOR = 10 * 60_000;
/** `extensions`: the lengths the summary offers to extend by, in minutes. */
export const SPRINT_EXTENSIONS = [5, 15];

/** `canExtend(at:)`: it ended a moment ago (not while the page was closed). */
export const canExtend = (s: WritingSprint, now: number) => s.ended !== null && now - s.ended < SPRINT_EXTENDABLE_FOR;

/** `extended(by:at:)`: running again until `minutes` from `now`, words and goal kept; the time since it ended is part of it. */
export function extendedSprint(s: WritingSprint, minutes: number, now: number): WritingSprint {
  return { ...s, ended: null, duration: Math.max(0, (now - s.started) / 1000) + Math.max(1, Math.floor(minutes)) * 60 };
}

/** `clock`: "12:05", or "1:02:05" past an hour (seconds rounded up). */
export function sprintClock(seconds: number): string {
  const s = Math.ceil(seconds);
  const p = (n: number) => String(n).padStart(2, '0');
  return s >= 3600 ? `${Math.floor(s / 3600)}:${p(Math.floor(s / 60) % 60)}:${p(s % 60)}` : `${Math.floor(s / 60)}:${p(s % 60)}`;
}

export const SPRINT_LOG_LIMIT = 500;

/** `SprintLog.add`: newest first, the oldest past 500 dropped. */
export function addToLog(log: WritingSprint[], s: WritingSprint): WritingSprint[] {
  return [s, ...log].slice(0, SPRINT_LOG_LIMIT);
}

/** `SprintLog.remove`: a sprint taken out (one being extended, filed again when it ends). */
export const removeFromLog = (log: WritingSprint[], id: string) => log.filter((s) => s.id !== id);

export const sprintsIn = (log: WritingSprint[], projectID?: string | null) => (projectID ? log.filter((s) => s.projectID === projectID) : log);

/** `bestRate`: the best rate among sprints of at least five minutes. */
export function bestRate(log: WritingSprint[], projectID?: string | null): WritingSprint | null {
  let best: WritingSprint | null = null;
  for (const s of sprintsIn(log, projectID)) {
    if (((s.ended ?? plannedEnd(s)) - s.started) / 1000 < 300) continue;
    if (!best || wordsPerMinute(s) > wordsPerMinute(best)) best = s;
  }
  return best;
}

/** `averageRate`: total written over total time (each sprint at least a minute). */
export function averageRate(log: WritingSprint[], projectID?: string | null): number | null {
  const list = sprintsIn(log, projectID);
  if (!list.length) return null;
  const minutes = list.reduce((n, s) => n + Math.max(1, ((s.ended ?? plannedEnd(s)) - s.started) / 60_000), 0);
  return list.reduce((n, s) => n + s.added, 0) / minutes;
}

/** The summary: "412 written, 38 cut (+374 net) in 25 minutes: 16 words a minute. Goal of 300 met." */
export function sprintSummary(s: WritingSprint, format: (n: number) => string = (n) => n.toLocaleString('en-GB')): string {
  const minutes = Math.round(((s.ended ?? plannedEnd(s)) - s.started) / 60_000);
  const net = sprintNet(s);
  let text = `${format(s.added)} written, ${format(s.removed)} cut (${net >= 0 ? '+' : '−'}${format(Math.abs(net))} net) in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}: ${format(Math.round(wordsPerMinute(s)))} words a minute.`;
  if (s.goal !== null) text += goalMet(s) ? ` Goal of ${format(s.goal)} met.` : ` Goal: ${format(s.goal)}.`;
  return text;
}
